import React, { useContext, useMemo, useState } from 'react';
import {
  ActionIcon,
  createStyles,
  Divider,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { CircleX, SquareCheck, X } from 'tabler-icons-react';
import IDelivery, { getPrefixedDeliveryId } from '../../models/IDelivery';
import { useTranslation } from 'react-i18next';
import { AbstractContextType } from '../../context/AbstractProvider';
import { DeliveryContext } from '../../context/ContextWrapper';
import IInquiry, { getPrefixedInquiryId } from '../../models/IInquiry';
import IOrderPosition from '../../models/IOrder';
import IVehicle from '../../models/IVehicle';
import ILocation from '../../models/ILocation';
import { DeliveryStatusElement } from '../inquiries/DeliveryStatusElement';
import {
  getPrefixedDeliveryPositionId,
  IDeliveryPosition,
} from '../../models/IDeliveryPosition';
import { showAppNotifcation } from '../../utility/NotificationConfigs';
import DeliveryPositionService from '../../services/DeliveryPositionService';
import { DELIVERY_STATUS_ENUM } from '../../api_enums/DELIVERY_STATUS_ENUM';
import { AuthContext, AuthContextType } from '../../context/AuthProvider';
import { ROLE_ENUM } from '../../api_enums/ROLE_ENUM';
import cx from 'clsx';
import ISection from '../../models/ISection';
import IResource from '../../models/IResource';

const useStyles = createStyles((theme) => ({
  header: {
    zIndex: 1,
    position: 'sticky',
    top: 0,
    backgroundColor:
      theme.colorScheme === 'dark'
        ? theme.colors.dark[6]
        : theme.colors.gray[0],
    transition: 'box-shadow 150ms ease',
    after: {
      content: "''",
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: 'rem(1px) solid',
    },
  },
  scrolled: {
    boxShadow: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)',
  },
  highlightedRow: {
    backgroundColor:
      theme.colorScheme === 'dark'
        ? `${theme.colors.blue[9]} !important`
        : `${theme.colors.blue[1]} !important`,
    transition: 'background-color 0.3s ease',
  },
}));

function DeliveriesList(props: { searchValue: string }) {
  const ctxDelivery = useContext(
    DeliveryContext,
  ) as AbstractContextType<IDelivery>;
  const { classes } = useStyles();
  const { userInfo } = useContext(AuthContext) as AuthContextType;
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [lastActionId, setLastActionId] = useState<number | null>(null);

  const onRowClick = (id: number) => {
    setLastActionId(id);
  };

  const isAdminOrManager = () => {
    return (
      userInfo?.user_roles.includes(ROLE_ENUM.ADMINISTRATOR) ||
      userInfo?.section_roles.includes(ROLE_ENUM.ADMINISTRATOR) ||
      userInfo?.user_roles.includes(ROLE_ENUM.MANAGER) ||
      userInfo?.section_roles.includes(ROLE_ENUM.MANAGER)
    );
  };

  const hasRecipientPermission = () => {
    return (
      isAdminOrManager() ||
      userInfo?.user_roles.includes(ROLE_ENUM.RECIPIENT) ||
      userInfo?.section_roles.includes(ROLE_ENUM.RECIPIENT)
    );
  };

  const getInquiryFromPosition = (position: IDeliveryPosition): IInquiry => {
    return (position.ordered_through as IOrderPosition)
      ?.inquired_through as IInquiry;
  };

  const getDeliveryLocation = (position: IDeliveryPosition): ILocation => {
    return getInquiryFromPosition(position)?.deliver_to as ILocation;
  };

  const getSectionFromPosition = (position: IDeliveryPosition): ISection => {
    return getDeliveryLocation(position)?.belongs_to as ISection;
  };

  const hasAccessToSection = (position: IDeliveryPosition): boolean => {
    return (
      getSectionFromPosition(position)?.id === userInfo?.section_id ||
      isAdminOrManager()
    );
  };

  const matchesSearch = (
    delivery: IDelivery,
    position: IDeliveryPosition,
  ): boolean => {
    if (props.searchValue === '') return true;

    const searchLower = props.searchValue.toLowerCase().trim();
    return (
      getPrefixedDeliveryId(delivery.id).toLowerCase().includes(searchLower) ||
      getPrefixedDeliveryPositionId(position.id)
        .toLowerCase()
        .includes(searchLower) ||
      getPrefixedInquiryId(getInquiryFromPosition(position)?.id)
        .toLowerCase()
        .includes(searchLower)
    );
  };

  const allRows = useMemo(() => {
    if (!ctxDelivery.entities) return [];

    return ctxDelivery.entities
      .sort((deliveryA, deliveryB) => {
        const hasOnDeliveryA = deliveryA.positions?.some(
          (pos) => pos.status === DELIVERY_STATUS_ENUM.ON_DELIVERY,
        );
        const hasOnDeliveryB = deliveryB.positions?.some(
          (pos) => pos.status === DELIVERY_STATUS_ENUM.ON_DELIVERY,
        );

        if (hasOnDeliveryA && !hasOnDeliveryB) return -1;
        if (!hasOnDeliveryA && hasOnDeliveryB) return 1;

        const dateA = new Date(deliveryA.created_at || 0).getTime();
        const dateB = new Date(deliveryB.created_at || 0).getTime();
        return dateB - dateA;
      })
      .flatMap((delivery) => {
        if (!delivery.positions) return [];

        return delivery.positions
          .filter((position) => {
            return (
              hasAccessToSection(position) && matchesSearch(delivery, position)
            );
          })
          .sort((posA, posB) => {
            const isOnDeliveryA =
              posA.status === DELIVERY_STATUS_ENUM.ON_DELIVERY;
            const isOnDeliveryB =
              posB.status === DELIVERY_STATUS_ENUM.ON_DELIVERY;

            if (isOnDeliveryA && !isOnDeliveryB) return -1;
            if (!isOnDeliveryA && isOnDeliveryB) return 1;

            return 0;
          })
          .map((position) => ({ delivery, position }));
      });
  }, [
    ctxDelivery.entities,
    ctxDelivery.lastUpdate,
    props.searchValue,
    userInfo?.section_id,
  ]);

  return (
    <Paper withBorder radius="md" p="xs">
      <Stack>
        <ScrollArea
          style={{ width: 'auto' }}
          type="always"
          h={'85vh'}
          onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
        >
          <Table striped verticalSpacing="xs" fontSize="md">
            <thead
              className={cx(classes.header, { [classes.scrolled]: scrolled })}
            >
              <tr>
                <th>{t('main.DeliveryNo')}</th>
                <th>{t('main.DeliveryPositionNo')}</th>
                <th>{t('main.InquiryNo')}</th>
                <th>{t('main.Status')}</th>
                <th>{t('main.Resource')}</th>
                <th>{t('AcceptDeliveryDialog.Vehicle')}</th>
                <th>{t('main.DeliveryLocation')}</th>
                <th>{t('main.Quantity')}</th>
                <th>{t('DeliveryForm.shippedAmount')}</th>
                <th>{t('DeliveryForm.Remaining')}</th>
                <th>{t('main.Received')}</th>
                <th>{t('main.Reject')}</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map(({ delivery, position }) => {
                const checkPermission = () => {
                  const isOnDelivery =
                    (position?.status as DELIVERY_STATUS_ENUM) ===
                    DELIVERY_STATUS_ENUM.ON_DELIVERY;
                  const hasRoleAccess = hasRecipientPermission();
                  const hasSectionAccess = hasAccessToSection(position);

                  return isOnDelivery && hasRoleAccess && hasSectionAccess;
                };

                let received_amount =
                  position.received_amount === 0 && checkPermission()
                    ? 1
                    : position.received_amount;

                const inquiry = getInquiryFromPosition(position);
                const location = getDeliveryLocation(position);
                const orderPosition =
                  position.ordered_through as IOrderPosition;
                const vehicle = delivery.delivered_through as IVehicle;

                return (
                  <tr
                    key={position?.id}
                    onClick={() => onRowClick(position.id)}
                    className={
                      lastActionId === position.id ? classes.highlightedRow : ''
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{getPrefixedDeliveryId(delivery?.id)}</td>
                    <td>{getPrefixedDeliveryPositionId(position?.id)}</td>
                    <td>{getPrefixedInquiryId(inquiry?.id)}</td>
                    <td>
                      <DeliveryStatusElement status={position?.status} />
                    </td>
                    <td>
                      {
                        (
                          (orderPosition.inquired_through as IInquiry)
                            ?.inquired_is as IResource
                        )?.name
                      }
                    </td>
                    <td>
                      {vehicle?.radio_call_name}
                      {vehicle?.license_plate && `(${vehicle.license_plate})`}
                    </td>
                    <td>{location?.name}</td>
                    <td>{orderPosition?.ordered_amount}</td>
                    <td>{position.shipped_amount}</td>
                    <td>{orderPosition?.remaining_amount}</td>
                    <td>
                      <Group>
                        <NumberInput
                          size="xs"
                          disabled={!checkPermission()}
                          style={{ width: 80 }}
                          defaultValue={position?.shipped_amount}
                          value={position.shipped_amount}
                          min={1}
                          max={position.shipped_amount}
                          onChange={(v) => {
                            received_amount = v;
                          }}
                          required
                        />
                        <ActionIcon
                          disabled={!checkPermission()}
                          onClick={() => {
                            DeliveryPositionService.receive(
                              position.id,
                              received_amount,
                            )
                              .then(() => {
                                showAppNotifcation(
                                  true,
                                  t('AcceptDeliveryDialog.ConfirmedReceive'),
                                );
                              })
                              .catch(() => {
                                showAppNotifcation(
                                  false,
                                  t('AcceptDeliveryDialog.CannotReceive'),
                                );
                              })
                              .finally();
                          }}
                          color="green"
                        >
                          <SquareCheck />
                        </ActionIcon>
                      </Group>
                    </td>
                    <td>
                      <ActionIcon
                        disabled={!checkPermission()}
                        onClick={() => {
                          DeliveryPositionService.reject(position.id)
                            .then(() => {
                              showAppNotifcation(
                                true,
                                t('AcceptDeliveryDialog.RejectReceive'),
                              );
                            })
                            .catch(() => {
                              showAppNotifcation(
                                false,
                                t('AcceptDeliveryDialog.CannotReject'),
                              );
                            })
                            .finally();
                        }}
                        color="red"
                      >
                        <CircleX />
                      </ActionIcon>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </ScrollArea>
        {allRows.length === 0 && (
          <Text align="center">{t('AcceptDeliveryDialog.NoDeliveries')}</Text>
        )}
      </Stack>
    </Paper>
  );
}

export function AcceptDeliveryTable() {
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();

  return (
    <>
      <TextInput
        placeholder={'DE-1 oder DP-1 oder BA-1'}
        label={t('AcceptDeliveryDialog.DeliverySearchLabel')}
        radius="md"
        size="md"
        rightSection={
          searchValue && (
            <ActionIcon onClick={() => setSearchValue('')}>
              <X />
            </ActionIcon>
          )
        }
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />
      <Divider my="sm" />
      <DeliveriesList searchValue={searchValue} />
    </>
  );
}
